import React from 'react';
import dayjs from 'dayjs';
import { CheckCircle2 } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';
import { Tooltip, TooltipContent, TooltipTrigger } from 'shadcn/components/ui/tooltip';
import { cn } from 'shadcn/lib/utils';
import { BulkUploadTable } from 'shared/components/bulkUpload';

import { isPurchaseOrderConfirmed } from './utils/purchaseOrderDisplayUtils';

function formatApiDate(value, format = 'MM/DD/YYYY') {
    if (!value) {
        return '';
    }

    if (typeof value === 'string') {
        return dayjs(value).format(format);
    }

    if (value.date) {
        return dayjs(value.date).format(format);
    }

    return '';
}

function VendorEmailedCell({ getValue, row }) {
    const value = getValue();
    const emailVendorErrors = row.original.data.emailVendorErrors ?? [];
    const hasEmailErrors = emailVendorErrors.length > 0;

    const cellContent = (
        <div
            className={cn(
                'px-2 py-1 -mx-2 -my-1',
                hasEmailErrors && 'bg-red-100 border-2 border-red-500 rounded cursor-help mr-2 ml-2',
            )}
        >
            {value ?? ''}
        </div>
    );

    if (hasEmailErrors) {
        return (
            <Tooltip>
                <TooltipTrigger asChild>{cellContent}</TooltipTrigger>
                <TooltipContent className="bg-red-100 text-black border-2 border-red-500 max-w-xs z-[2101]">
                    {emailVendorErrors.length > 0 ? emailVendorErrors.join('. ') : 'Something went wrong'}
                </TooltipContent>
            </Tooltip>
        );
    }

    return cellContent;
}

function getPurchaseOrderId(purchaseOrder) {
    return purchaseOrder.purchaseOrderId ?? purchaseOrder.id;
}

function getEmailVendorErrors(emailResult, fallback = {}) {
    const emailResultErrors = emailResult?.emailVendorErrors ?? [];
    const fallbackErrors = fallback?.emailVendorErrors ?? [];

    return [...new Set(emailResultErrors.length > 0 ? emailResultErrors : fallbackErrors)];
}

function getVendorEmailed(emailResult, fallback = {}) {
    return (
        emailResult?.vendorEmailed ??
        emailResult?.wasVendorEmailed ??
        fallback?.vendorEmailed ??
        fallback?.wasVendorEmailed ??
        false
    );
}

function formatPurchaseOrderCount(count, action) {
    return `${count} ${count === 1 ? 'PO' : 'POs'} ${action}`;
}

function getPurchaseOrdersWithEmailErrors(purchaseOrders, emailResultByPoId = {}) {
    return purchaseOrders
        .map((purchaseOrder) => {
            const purchaseOrderId = getPurchaseOrderId(purchaseOrder);
            const errors = getEmailVendorErrors(emailResultByPoId[purchaseOrderId], purchaseOrder);

            return {
                id: purchaseOrderId,
                errors,
            };
        })
        .filter((purchaseOrder) => purchaseOrder.errors.length > 0);
}

function formatConfirmedForSummary(purchaseOrder) {
    return isPurchaseOrderConfirmed(purchaseOrder) ? 'Yes' : 'No';
}

function mapSuccessfulPurchaseOrdersToTableRows(successfulPurchaseOrders, emailResultByPoId = {}) {
    return successfulPurchaseOrders.map((po, index) => {
        const purchaseOrderId = getPurchaseOrderId(po);
        const emailResult = emailResultByPoId[purchaseOrderId];

        return {
            rowIndex: po.row ?? index,
            data: {
                id: po.id ?? po.purchaseOrderId,
                purchaseOrderId: po.id ?? po.purchaseOrderId,
                action: po.action ?? '',
                vendorEmailed: getVendorEmailed(emailResult, po) ? 'Yes' : 'No',
                emailVendorErrors: getEmailVendorErrors(emailResult, po),
                status: po.status ?? '',
                vendorName: po.vendorName ?? '',
                facilityName: po.facilityName ?? '',
                shipperName: po.shipperName ?? '',
                deliveryType: po.deliveryType ?? '',
                pickupDate: formatApiDate(po.pickupDate),
                scheduledDeliveryDate: formatApiDate(po.scheduledDeliveryDate, 'MM/DD/YYYY HH:mm:ss'),
                crossDockName: po.crossDockName ?? '',
                crossDockDeliveryDate: formatApiDate(po.crossDockDeliveryDate),
                poTotalCost: po.poTotalCost ?? '',
                buyers: Array.isArray(po.buyers) ? po.buyers.map((buyer) => buyer.name).join(', ') : '',
                minTempReq: po.minTempReq ?? '',
                maxTempReq: po.maxTempReq ?? '',
                specialInstructions: po.specialInstructions ?? '',
                priority: po.priority ?? '',
                confirmed: formatConfirmedForSummary(po),
                estimatedFreightCost: po.estimatedFreightCost ?? '',
                createdAt: formatApiDate(po.createdAt),
            },
        };
    });
}

const PERSIST_SUCCESS_COLUMN_DEFS = [
    { key: 'id', label: 'PO #', required: false, conditionallyRequired: false },
    { key: 'action', label: 'Action', required: false, conditionallyRequired: false },
    { key: 'vendorEmailed', label: 'Vendor Emailed', required: false, conditionallyRequired: false },
    { key: 'createdAt', label: 'Created On', required: false, conditionallyRequired: false },
    { key: 'status', label: 'Status', required: false, conditionallyRequired: false },
    { key: 'deliveryType', label: 'Delivery Type', required: false, conditionallyRequired: false },
    { key: 'pickupDate', label: 'Pick Up Date', required: false, conditionallyRequired: false },
    { key: 'scheduledDeliveryDate', label: 'Scheduled Delivery', required: false, conditionallyRequired: false },
    { key: 'vendorName', label: 'Vendor', required: false, conditionallyRequired: false },
    { key: 'shipperName', label: 'Shipper', required: false, conditionallyRequired: false },
    { key: 'crossDockName', label: 'Cross Dock', required: false, conditionallyRequired: false },
    { key: 'crossDockDeliveryDate', label: 'Cross Dock Delivery Date', required: false, conditionallyRequired: false },
    { key: 'facilityName', label: 'Facility', required: false, conditionallyRequired: false },
    { key: 'poTotalCost', label: 'Total Cost', required: false, conditionallyRequired: false },
    { key: 'buyers', label: 'Buyers', required: false, conditionallyRequired: false },
    { key: 'minTempReq', label: 'Min Temperature', required: false, conditionallyRequired: false },
    { key: 'maxTempReq', label: 'Max Temperature', required: false, conditionallyRequired: false },
    { key: 'specialInstructions', label: 'Special Instructions', required: false, conditionallyRequired: false },
    { key: 'priority', label: 'Priority', required: false, conditionallyRequired: false },
    { key: 'confirmed', label: 'Confirmed', required: false, conditionallyRequired: false },
    { key: 'estimatedFreightCost', label: 'Freight Cost', required: false, conditionallyRequired: false },
];

const PERSIST_SUCCESS_COLUMN_DEFS_WITH_CELLS = PERSIST_SUCCESS_COLUMN_DEFS.map((def) =>
    def.key === 'vendorEmailed' ? { ...def, cell: VendorEmailedCell } : def,
);

function BulkCreatePurchaseOrderSummary({ bulkUpload, onStartNew }) {
    const emailResultByPoId = Object.fromEntries(
        bulkUpload.emailVendorResults.map((result) => [result.purchaseOrderId, result]),
    );

    const persistSuccessRows = mapSuccessfulPurchaseOrdersToTableRows(
        bulkUpload.successfulPurchaseOrders,
        emailResultByPoId,
    );

    const purchaseOrdersWithEmailErrors = getPurchaseOrdersWithEmailErrors(
        bulkUpload.successfulPurchaseOrders,
        emailResultByPoId,
    );
    const hasEmailVendorErrors = purchaseOrdersWithEmailErrors.length > 0;

    const createdPoCount = new Set(
        bulkUpload.successfulPurchaseOrders
            .filter((po) => po.action === 'add')
            .map((po) => po.purchaseOrderId ?? po.id),
    ).size;
    const updatedPoCount = new Set(
        bulkUpload.successfulPurchaseOrders
            .filter((po) => po.action !== 'add')
            .map((po) => po.purchaseOrderId ?? po.id),
    ).size;

    return (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                        <p className="text-lg font-medium">
                            {bulkUpload.persistErrors.length > 0 || hasEmailVendorErrors
                                ? 'Upload complete with errors'
                                : 'Upload complete'}
                        </p>
                        <CheckCircle2 className="h-6 w-6 text-green-500" aria-hidden="true" />
                    </div>
                    {(createdPoCount > 0 || updatedPoCount > 0) && (
                        <p className="text-sm text-muted-foreground" data-testid="persist-summary-counts">
                            {createdPoCount > 0 && formatPurchaseOrderCount(createdPoCount, 'created')}
                            {createdPoCount > 0 && updatedPoCount > 0 && ', '}
                            {updatedPoCount > 0 && formatPurchaseOrderCount(updatedPoCount, 'updated')}
                        </p>
                    )}
                </div>
                <Button onClick={onStartNew}>Upload Another File</Button>
            </div>
            {bulkUpload.persistErrors.length > 0 && (
                <Alert variant="destructive" data-testid="persist-errors">
                    <AlertTitle>
                        {bulkUpload.persistErrors.length} row{bulkUpload.persistErrors.length !== 1 ? 's' : ''} failed
                        to save
                    </AlertTitle>
                    <AlertDescription className="w-full min-w-0 justify-items-stretch mt-1">
                        <ul
                            className="max-h-40 w-full min-w-0 list-disc space-y-1 overflow-y-auto pl-4 [scrollbar-gutter:stable]"
                            aria-label="Row save errors"
                        >
                            {bulkUpload.persistErrors.map((error) => (
                                <li key={`persist-error-${error.row}`}>
                                    Row {error.row + 1}: {error.message}
                                </li>
                            ))}
                        </ul>
                        {bulkUpload.persistErrors.length > 5 && (
                            <p className="w-full text-xs text-destructive/80 mt-1">
                                Scroll to view all {bulkUpload.persistErrors.length} errors.
                            </p>
                        )}
                    </AlertDescription>
                </Alert>
            )}
            {hasEmailVendorErrors && (
                <Alert variant="destructive" data-testid="vendor-email-errors">
                    <AlertTitle>
                        {purchaseOrdersWithEmailErrors.length} purchase order
                        {purchaseOrdersWithEmailErrors.length !== 1 ? 's' : ''} failed to email the vendor
                    </AlertTitle>
                    <AlertDescription className="w-full min-w-0 justify-items-stretch mt-1">
                        <ul
                            className="max-h-40 w-full min-w-0 list-disc space-y-1 overflow-y-auto pl-4 [scrollbar-gutter:stable]"
                            aria-label="Vendor email errors"
                        >
                            {purchaseOrdersWithEmailErrors.map((po) => (
                                <li key={`vendor-email-error-${po.id}`}>
                                    PO #{po.id}: {po.errors.join('. ')}
                                </li>
                            ))}
                        </ul>
                        {purchaseOrdersWithEmailErrors.length > 5 && (
                            <p className="w-full text-xs text-destructive/80 mt-1">
                                Scroll to view all {purchaseOrdersWithEmailErrors.length} errors.
                            </p>
                        )}
                    </AlertDescription>
                </Alert>
            )}
            <BulkUploadTable
                columnDefs={PERSIST_SUCCESS_COLUMN_DEFS_WITH_CELLS}
                errorsByCell={new Map()}
                fillAvailableHeight
                rows={persistSuccessRows}
            />
        </div>
    );
}

export {
    BulkCreatePurchaseOrderSummary,
    VendorEmailedCell,
    getPurchaseOrdersWithEmailErrors,
    mapSuccessfulPurchaseOrdersToTableRows,
};
